import ApiError from '../models/api-error';
import ErrorCode from '../models/error-code';
import AuthInfo from '../models/auth-info';

import StorageService from './storage-service';

const REFRESH_TIMEOUT_MS = 15000;

let pendingRefresh: Promise<void> = null;

// Requests that 401 together would each send the same refresh token. The API rotates it on first use,
// so all but one would get RefreshTokenNotFound and log the user out. One refresh per tab, and the
// lock serializes tabs, which share the token through localStorage.
function refreshAccessToken(staleAccessToken: string): Promise<void> {
  if (!pendingRefresh) {
    pendingRefresh = withRefreshLock(() => refreshUnlessDone(staleAccessToken)).finally(() => {
      pendingRefresh = null;
    });
  }

  return pendingRefresh;
}

function withRefreshLock(refresh: () => Promise<void>): Promise<void> {
  if (!navigator.locks) {
    return refresh();
  }

  // <void>: lib.dom would type the result Promise<Promise<void>>, but the lock settles with the callback's
  // promise. A page without a normal origin (sandboxed iframe, file://) has navigator.locks but cannot
  // take a lock: request() rejects with SecurityError before running the callback, so refreshing
  // without the lock cannot refresh twice.
  return navigator.locks.request<void>('edudo-admin-refresh', refresh)
    .catch(error => error?.name === 'SecurityError' ? refresh() : Promise.reject(error));
}

async function refreshUnlessDone(staleAccessToken: string): Promise<void> {
  const storageService = new StorageService();
  const auth = storageService.getAuthInfo();

  if (!auth) {
    throw new ApiError(ErrorCode.Unknown, 'Sessão encerrada');
  }

  if (auth.accessToken !== staleAccessToken) {
    return;
  }

  // fetch has no timeout of its own, and a refresh that never answers would hold the lock every tab's
  // refresh waits on. Aborting fails the refresh, which logs the user out like any failed refresh.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);

  try {
    const response = await fetch(HttpService.baseUrl.concat('/v1/authentication/login'), {
      method: 'POST',
      mode: 'cors',
      headers: getDefaultHeaders('application/json'),
      body: JSON.stringify({
        employeeId: auth.employee.id,
        refreshToken: auth.refreshToken,
        grantType: 'RefreshToken'
      }),
      signal: controller.signal,
    });
    const json = await response.json();

    if (!response.ok) {
      throw json;
    }

    const authInfo: AuthInfo = Object.assign(new AuthInfo(), json.data);
    authInfo.calculateExpirationDate();
    storageService.setAuthInfo(authInfo);
  } finally {
    clearTimeout(timeout);
  }
}

function handleError<T>(
  error: ApiError,
  response: Response,
  request: (newHeaders?: HeadersInit) => Promise<Response>,
  accessToken: string,
  retried: boolean
) {
  const storageService = new StorageService();

  if (response.status === 401) {
    if (retried) {
      return revokeAuth(storageService, error);
    }

    return refreshAccessToken(accessToken).then(
      () => handleResponse<T>(() => request(getDefaultHeaders()), true),
      refreshError => revokeAuth(storageService, refreshError)
    );
  } else if (
    response.status === 400 &&
    (
      error.code === ErrorCode.RefreshTokenNotFound ||
      error.code === ErrorCode.CredentialExpired ||
      error.code === ErrorCode.UserNotActive
    )
  ) {
    return revokeAuth(storageService, error);
  }

  return Promise.reject(error);
}

function revokeAuth(storageService: StorageService, error?: any) {
  storageService.removeAuthInfo();

  window.location.replace('/login');

  return Promise.reject(error);
}

async function handleResponse<T>(request: (newHeaders?: HeadersInit) => Promise<Response>, retried = false): Promise<T> {
  let error = new ApiError(ErrorCode.Unknown, 'Erro desconhecido');
  const accessToken = new StorageService().getAccessToken();
  const response = await request();

  try {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.indexOf('application/json') !== -1) {
      const json = await response.json();

      if (response.ok) {
        return json;
      }

      if (json.code !== undefined && json.code !== null) {
        error = json;
      } else {
        // As rotas dos módulos respondem erro sem o envelope legado: 404/409 { error }, 400 { errors }.
        error.status = response.status;
        error.body = json;
      }

      return handleError<T>(error, response, request, accessToken, retried);
    } else {
      if (response.ok) {
        return;
      }

      // Um 403 dos módulos (Results.Forbid) vem sem corpo.
      error.status = response.status;
      return handleError<T>(error, response, request, accessToken, retried);
    }
  } catch (e) {
    console.log(e)
    return handleError<T>(error, response, request, accessToken, retried);
  }
}

function getDefaultHeaders(contentType?: string) {
  const storageService = new StorageService();
  const accessToken = storageService.getAccessToken();
  let authorization = '';
  if (accessToken) {
    authorization = `Bearer ${accessToken}`;
  }

  const headers = {
    Authorization: authorization,
    'X-Client-Info': `admin-web/${process.env.REACT_APP_VERSION}`
  };

  if (contentType) headers['Content-Type'] = contentType;

  return headers;
}

class HttpService {
  static baseUrl = process.env.REACT_APP_API || 'http://localhost:5000';

  static async postJson<T>(
    url: string,
    body: object,
    headers = {}
  ): Promise<T> {
    const fullUrl = this.baseUrl.concat(url);
    const requestHeaders = Object.assign({}, headers, getDefaultHeaders('application/json'));
    const request = (newHeaders?: HeadersInit) => fetch(fullUrl, {
      method: 'POST',
      mode: 'cors',
      headers: newHeaders ? Object.assign(requestHeaders, newHeaders) : requestHeaders,
      body: JSON.stringify(body),
    });

    return await handleResponse<T>(request);
  }

  static async patchJson<T>(url: string, body: object, headers = {}): Promise<T> {
    const fullUrl = this.baseUrl.concat(url);
    const requestHeaders = Object.assign({}, headers, getDefaultHeaders('application/json'));
    const request = (newHeaders?: HeadersInit) => fetch(fullUrl, {
      method: 'PATCH',
      mode: 'cors',
      headers: newHeaders ? Object.assign(requestHeaders, newHeaders) : requestHeaders,
      body: JSON.stringify(body),
    });

    return await handleResponse<T>(request);
  }

  static async putJson<T>(url: string, body: object, headers = {}): Promise<T> {
    const fullUrl = this.baseUrl.concat(url);
    const requestHeaders = Object.assign({}, headers, getDefaultHeaders('application/json'));
    const request = (newHeaders?: HeadersInit) => fetch(fullUrl, {
      method: 'PUT',
      mode: 'cors',
      headers: newHeaders ? Object.assign(requestHeaders, newHeaders) : requestHeaders,
      body: JSON.stringify(body),
    });

    return await handleResponse<T>(request);
  }

  static async getJson<T>(url: string, headers = {}): Promise<T> {
    const fullUrl = this.baseUrl.concat(url);
    const requestHeaders = Object.assign({}, headers, getDefaultHeaders('application/json'));
    const request = (newHeaders?: HeadersInit) => fetch(fullUrl, {
      method: 'GET',
      mode: 'cors',
      headers: newHeaders ? Object.assign(requestHeaders, newHeaders) : requestHeaders,
    });

    return await handleResponse<T>(request);
  }

  static async delete(url: string, headers = {}) {
    const fullUrl = this.baseUrl.concat(url);
    const requestHeaders = Object.assign({}, headers, getDefaultHeaders('application/json'));
    const request = (newHeaders?: HeadersInit) => fetch(fullUrl, {
      method: 'DELETE',
      mode: 'cors',
      headers: newHeaders ? Object.assign(requestHeaders, newHeaders) : requestHeaders,
    });

    return await handleResponse(request);
  }

  static async deleteJson<T>(url: string, body: object, headers = {}): Promise<T> {
    const fullUrl = this.baseUrl.concat(url);
    const requestHeaders = Object.assign({}, headers, getDefaultHeaders('application/json'));
    const request = (newHeaders?: HeadersInit) => fetch(fullUrl, {
      method: 'DELETE',
      mode: 'cors',
      headers: newHeaders ? Object.assign(requestHeaders, newHeaders) : requestHeaders,
      body: JSON.stringify(body),
    });

    return await handleResponse<T>(request);
  }

  static async uploadFiles<T>(
    url: string,
    body: FormData,
    headers = {}
  ): Promise<T> {
    const fullUrl = this.baseUrl.concat(url);
    const requestHeaders = Object.assign({}, headers, getDefaultHeaders());
    const request = (newHeaders?: HeadersInit) => fetch(fullUrl, {
      method: 'POST',
      mode: 'cors',
      headers: newHeaders ? Object.assign(requestHeaders, newHeaders) : requestHeaders,
      body,
    });

    return await handleResponse<T>(request);
  }
}

export default HttpService;
